import { __isKnytReference, isReadableReference, normalizeSubscriber, OBSERVABLE_PROPERTY_NAME, unwrapSubscriber, } from "@knyt/artisan";
import { hold } from "./hold.js";
/**
 * A controller that manages the state of an input element.
 *
 * @alpha This is an experimental API and will change in the future.
 */
export class InputStateController {
    #host;
    #options;
    #inputValue$;
    inputValue$;
    #observer;
    constructor(host, options) {
        this.#host = host;
        this.#options = options;
        const { origin: origin$, serialize } = options;
        const initialInputValue = serialize(origin$.value);
        this.#inputValue$ = hold(host, initialInputValue);
        this.inputValue$ = this.#inputValue$.asReadonly();
        const subscriberSource = options.subscriber;
        this.#observer = isReadableReference(subscriberSource)
            ? unwrapSubscriber(subscriberSource)
            : normalizeSubscriber(subscriberSource);
        host.addController(this);
        origin$.subscribe(this);
    }
    get value() {
        // This should be proxy to the origin reference.
        return this.#options.origin.get();
    }
    set value(nextValue) {
        // This should NOT be a proxy to the origin reference.
        // It should call the `set` method instead.
        this.set(nextValue);
    }
    get inputValue() {
        return this.#inputValue$.get();
    }
    subscribe(input) {
        // This should be a proxy to the origin reference.
        return this.#options.origin.subscribe(input);
    }
    get() {
        // This should be a proxy to the origin reference.
        return this.#options.origin.get();
    }
    asReadonly() {
        // This should be a proxy to the origin reference.
        return this.#options.origin;
    }
    /**
     * Set the value of the input state.
     *
     * @remarks
     *
     * 1. Updates the origin reference
     * 2. Updates the input value state
     * 3. Notifies the observe subscriber of the new value
     */
    set(nextValue) {
        const { serialize } = this.#options;
        this.#inputValue$.set(serialize(nextValue));
        this.#observer.next(nextValue);
    }
    next(nextValue) {
        // This should NOT be a proxy to the origin reference.
        // It should call the `set` method instead.
        this.set(nextValue);
    }
    /**
     * A handler that updates the input state when the element value changes.
     */
    handleChange = (event) => {
        const { parse } = this.#options;
        // While it's unlikely that the event target will be null,
        // it's possible in some edge cases. As a result, we fallback
        // to an empty string to ensure that some value is always passed.
        const value = event.target?.value ?? "";
        this.set(parse(value));
    };
    /**
     * A handler that updates the input state when the end user
     * interacts with the input element.
     */
    /*
     * ### Private Remarks
     *
     * This is an alias for the `handleChange` handler,
     * but it's provided for consistency, clarity, and future-proofing.
     */
    handleInput = this.handleChange;
    hostConnected;
    get [__isKnytReference]() {
        return true;
    }
    asInterop() {
        return this.#options.origin.asInterop();
    }
    [Symbol.observable]() {
        return this.asInterop();
    }
    [OBSERVABLE_PROPERTY_NAME]() {
        return this.asInterop();
    }
}
